import { defineComponent } from 'vue';
export default defineComponent({
    name: 'CatalogItem',
    emits: {
        addToCart: (payload) => true,
        viewDetails: (payload) => true,
        addToWishlist: (payload) => true,
    },
    props: {
        host: {
            type: String,
            required: true,
        },
        lang: {
            type: String,
            required: true,
        },
        product: {
            type: Object,
            required: true,
        },
        taxApplier: {
            type: Function,
            required: true,
        },
        showAddToCartBtn: {
            type: Boolean,
            default: true,
        },
        showQuantityInput: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            selectedId: this.product.id,
            quantity: 1,
        };
    },
    computed: {
        productLink() {
            switch (this.lang) {
                case 'it-IT':
                    return 'p';
                case 'en-GB':
                    return 'en/p';
                default:
                    return 'p';
            }
        },
        sellable() {
            return this.isStockStatusSellable(this.product.stockStatus);
        },
        category() {
            let cat = this.product.taxonomies['product_cat']?.[0];
            if (!cat) {
                return undefined;
            }
            while (cat.children.length !== 0) {
                cat = cat.children[0];
            }
            return cat.name;
        },
        categorySlug() {
            let cat = this.product.taxonomies['product_cat']?.[0];
            if (!cat) {
                return undefined;
            }
            while (cat.children.length !== 0) {
                cat = cat.children[0];
            }
            return cat.slug;
        },
        tags() {
            const tags = this.product.taxonomies['product_tag'] ?? [];
            const flat = [];
            const flatten = (items) => {
                for (const tag of items) {
                    if (tag.name && tag.slug) {
                        flat.push({ name: tag.name, slug: tag.slug });
                    }
                    if (tag.children?.length) {
                        flatten(tag.children);
                    }
                }
            };
            flatten(tags);
            return flat;
        },
        tagSlug() {
            console.log('product_tag:', this.product.taxonomies);
            let tag = this.product.taxonomies['product_tag']?.[0];
            if (!tag) {
                return undefined;
            }
            while (tag.children.length !== 0) {
                tag = tag.children[0];
            }
            return tag.slug;
        },
        modelTags() {
            const models = this.product.taxonomies['product_model'] ?? [];
            const flat = [];
            const flatten = (items) => {
                for (const model of items) {
                    if (model.name && model.slug) {
                        flat.push({ name: model.name, slug: model.slug });
                    }
                    if (model.children?.length) {
                        flatten(model.children);
                    }
                }
            };
            flatten(models);
            return flat;
        },
        image() {
            return (this.product.image?.sizes['woocommerce_large']?.link ??
                this.product.image?.link);
        },
        secondaryImage() {
            return (this.product.secondaryImage?.sizes['woocommerce_thumbnail']?.link ??
                this.product.secondaryImage?.link);
        },
        priceFormatter() {
            return new Intl.NumberFormat(this.lang, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
                style: 'currency',
                currency: this.product.currency,
            });
        },
        minPrice() {
            return this.stringifyPrice(this.product.minPrice, this.product.taxClass);
        },
        minBasePrice() {
            return this.stringifyPrice(this.product.minBasePrice, this.product.taxClass);
        },
        maxPrice() {
            return this.stringifyPrice(this.product.maxPrice, this.product.taxClass);
        },
        maxBasePrice() {
            return this.stringifyPrice(this.product.maxBasePrice, this.product.taxClass);
        },
        ratingStarClasses() {
            const classes = [];
            for (let i = 1; i <= 5; i++) {
                if (i <= this.product.averageRating) {
                    classes.push('fas fa-star');
                }
                else if (i <= this.product.averageRating + 0.5) {
                    classes.push('fas fa-star-half-alt');
                }
                else {
                    classes.push('far fa-star');
                }
            }
            return classes;
        },
        allSalePercentage() {
            if (this.product.catalogData?.variations) {
                let percentages = [];
                for (let p of this.product.catalogData.variations.products) {
                    percentages.push(Math.round(100 - (p.price * 100) / p.basePrice));
                }
                return percentages;
            }
            return [
                Math.round(100 - (this.product.minPrice * 100) / this.product.minBasePrice)
            ];
        },
        minPriceSalePercentage() {
            return Math.round(100 - (this.product.minPrice * 100) / this.product.minBasePrice).toString();
        },
        minSalePercentage() {
            return Math.min(...this.allSalePercentage).toString();
        },
        maxSalePercentage() {
            return Math.max(...this.allSalePercentage).toString();
        },
        jvmWishListExists() {
            return typeof JVMWooCommerceWishlist === 'object';
        },
        isAccessoriesCategory() {
            let cat = this.product.taxonomies['product_cat']?.[0];
            if (!cat)
                return false;
            while (cat.children.length !== 0) {
                if (cat.slug === 'accessories')
                    return true;
                cat = cat.children[0];
            }
            return cat.slug === 'accessories';
        },
        stockText() {
            const value = this.product?.metadata?.stock_static;
            if (!value)
                return ''; // se è null, undefined o stringa vuota, ritorna ''
            return this.isNumeric(value) ? `Edition of  ${value}` : value;
        }
    },
    mounted() {
        // todo: this is not efficient
        if (this.jvmWishListExists) {
            JVMWooCommerceWishlist.build();
        }
        if (this.product.type === 'variable' &&
            this.product.catalogData?.variations) {
            for (const v of this.product.catalogData.variations.products) {
                if (this.isStockStatusSellable(v.stockStatus)) {
                    this.selectedId = v.id.toString();
                    break;
                }
            }
        }
    },
    methods: {
        stringifyPrice(price, taxClass) {
            return this.priceFormatter.format(this.taxApplier(price, taxClass));
        },
        increaseQuantity(amount) {
            this.quantity += amount;
            if (this.quantity < 1) {
                this.quantity = 1;
            }
        },
        isStockStatusSellable(status) {
            return ['instock', 'onbackorder'].includes(status);
        },
        isNumeric(value) {
            return ((typeof value === 'string' || typeof value === 'number') &&
                !isNaN(Number(value)) &&
                isFinite(Number(value)));
        }
    },
});
